import React, { useCallback, useMemo, useState } from "react";
import { useForm, useFormState } from "react-final-form";
import { useSelector } from "react-redux";
import classNames from "classnames";
import Input from "rs-components/Input";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import SelectInput from "rs-components/Input/components/SelectInput";
import ToggleButtonInput from "rs-components/Input/components/ToggleButtonInput";
import { FormField } from "../../index";
import HiddenInput from "rs-components/Input/components/HiddenInput";
import { isNotEmpty } from "utils/validate";
import { hasOptimizationUsingGivenVariant } from "store/rs-workspace/utils/scenario.utils";
import { getAvailableFilteredSimulations, getResultElementSourceOptimizationOptions, getResultVariantsOptions, } from "store/rs-workspace/utils/results.utils";
import { incompleteSimulationsIdsSelector, workspaceCellModelsSelector, workspaceInsightsSelector, workspaceOptimizationsSelector, workspaceRecipesSelector, workspaceSimulationsSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { processVariablesSelector } from "store/global/global.selectors";
import * as styles from "../../styles.module.scss";
const SimulationSourceFormField = ({ withOptimization, sourcesForFilter }) => {
    const form = useForm();
    const formState = useFormState();
    const { simulationId, optimizationId, adjustmentId } = useMemo(() => ({ ...formState.values }), [formState.values]);
    const optimizations = useSelector(workspaceOptimizationsSelector);
    const insights = useSelector(workspaceInsightsSelector);
    const processVariables = useSelector(processVariablesSelector);
    const incompleteSimulationIds = useSelector(incompleteSimulationsIdsSelector);
    const simulations = useSelector(workspaceSimulationsSelector);
    const cellModels = useSelector(workspaceCellModelsSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const incompleteSimulationsIds = useSelector(incompleteSimulationsIdsSelector);
    const [optimizationEnabled, setOptimizationEnabled] = useState(isNotEmpty(optimizationId) && !!withOptimization);
    const simulationsOptions = useMemo(() => getAvailableFilteredSimulations(simulations, optimizations, insights, cellModels, recipes, processVariables, incompleteSimulationsIds, sourcesForFilter).filteredSimulations.map(({ name, id }) => ({
        value: id,
        label: name,
    })), [
        cellModels,
        incompleteSimulationsIds,
        insights,
        optimizations,
        processVariables,
        recipes,
        simulations,
        sourcesForFilter,
    ]);
    const optimizationOptions = useMemo(() => getResultElementSourceOptimizationOptions(simulationId, adjustmentId, optimizations, insights, processVariables, incompleteSimulationIds, simulations, cellModels, recipes, optimizationId), [
        incompleteSimulationIds,
        insights,
        optimizationId,
        optimizations,
        processVariables,
        simulationId,
        adjustmentId,
        simulations,
        cellModels,
        recipes,
    ]);
    const noSources = useMemo(() => simulationsOptions.length === 0, [simulationsOptions.length]);
    const noOptimizations = useMemo(() => optimizationOptions.length === 0, [optimizationOptions.length]);
    const isOptimizationToggleDisabled = useCallback((variantId) => {
        const variantIdToCheck = isNotEmpty(variantId) ? variantId : adjustmentId;
        return (!isNotEmpty(variantIdToCheck) ||
            !hasOptimizationUsingGivenVariant(simulationId, variantIdToCheck, optimizations, insights));
    }, [adjustmentId, insights, optimizations, simulationId]);
    const getVariantsOptions = useCallback((simulationId) => getResultVariantsOptions(simulations, cellModels, recipes, optimizations, simulationId, optimizationId ?? undefined, adjustmentId, sourcesForFilter), [simulations, cellModels, recipes, optimizations, optimizationId, adjustmentId, sourcesForFilter]);
    const handleChangeOptimizationToggle = useCallback((isChecked) => {
        setOptimizationEnabled(isChecked);
        !isChecked && form.change(FormField.optimizationId, undefined);
    }, [form]);
    const onSimulationChange = useCallback((simulationId) => {
        const simulation = simulations?.find(({ id }) => id === simulationId);
        const simulationVariants = simulation?.state?.adjustments ?? [];
        form.batch(() => {
            form.change(FormField.adjustmentId, simulationVariants?.length === 0 ? 0 : undefined);
            form.change(FormField.optimizationId, undefined);
        });
        setOptimizationEnabled(false);
    }, [form, simulations]);
    const onVariantChange = useCallback((variantId) => {
        if (isOptimizationToggleDisabled(variantId)) {
            form.change(FormField.optimizationId, undefined);
            setOptimizationEnabled(false);
        }
    }, [form, isOptimizationToggleDisabled]);
    return (React.createElement("div", { className: styles.simulationForm },
        React.createElement("div", { className: styles.field },
            React.createElement(Input, { itemRenderer: SelectItemRenderer, classes: {
                    component: classNames(styles.select, {
                        [styles.noSources]: noSources,
                    }),
                    label: styles["field-label"],
                }, component: SelectInput, name: FormField.simulationId, label: "Simulation", valueFormatter: ({ value }) => value, onChange: onSimulationChange, items: simulationsOptions, placeholder: !simulationsOptions.length ? "No Simulations to add." : "Select", restrictions: { IsMandatory: true } })),
        isNotEmpty(simulationId) && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: styles.field },
                React.createElement(Input, { component: SelectInput, itemRenderer: SelectItemRenderer, classes: { component: styles.select, label: styles["field-label"] }, name: FormField.adjustmentId, valueFormatter: ({ value }) => value, label: "Variant", items: getVariantsOptions(simulationId), placeholder: "Select", restrictions: { IsMandatory: true }, onChange: onVariantChange })),
            !!withOptimization && (React.createElement(React.Fragment, null,
                React.createElement("div", { className: classNames(styles.field, styles.optimizationToggle, {
                        [styles.end]: optimizationEnabled,
                    }) },
                    !optimizationEnabled && "Optimization",
                    React.createElement(ToggleButtonInput, { value: optimizationEnabled, onChange: handleChangeOptimizationToggle, className: styles.toggle, disabled: isOptimizationToggleDisabled() })),
                optimizationEnabled ? (React.createElement("div", { className: classNames(styles.field, { [styles.shifted]: optimizationEnabled }) },
                    React.createElement(Input, { component: SelectInput, itemRenderer: SelectItemRenderer, classes: {
                            component: styles.select,
                            label: classNames(styles.optimizationLabel, styles["field-label"]),
                        }, name: FormField.optimizationId, valueFormatter: ({ value }) => value, label: "Optimization", disabled: noOptimizations, items: optimizationOptions, restrictions: { IsMandatory: true }, placeholder: noOptimizations ? "No Optimizations to add." : "Select" }))) : (React.createElement(Input, { component: HiddenInput, suppressErrors: true, name: FormField.optimizationId }))))))));
};
export default SimulationSourceFormField;
